/**
 * Email Style Scoping Utilities
 * 
 * This utility helps prevent email CSS from affecting the entire application
 * by renaming CSS classes and IDs to use unique prefixes that won't conflict with app styles.
 */

/**
 * Renames CSS classes and IDs in both CSS and HTML to prevent conflicts
 */
export const renameCSSClasses = (css: string, html: string, prefix: string = 'email'): { css: string; html: string } => {
  // Find all class and ID selectors in CSS
  const classSelectors = new Set<string>();
  const idSelectors = new Set<string>();
  
  // Extract class selectors (e.g., .header, .button)
  const classMatches = css.match(/\.[a-zA-Z][a-zA-Z0-9_-]*/g) || [];
  classMatches.forEach(match => {
    const className = match.substring(1); // Remove the dot
    classSelectors.add(className);
  });
  
  // Extract ID selectors (e.g., #header, #button)
  const idMatches = css.match(/#[a-zA-Z][a-zA-Z0-9_-]*/g) || [];
  idMatches.forEach(match => {
    const idName = match.substring(1); // Remove the hash
    idSelectors.add(idName);
  });
  
  let updatedCSS = css;
  let updatedHTML = html;
  
  // Rename classes in CSS and HTML
  classSelectors.forEach(className => {
    const newClassName = `${prefix}-${className}`;
    
    // Update CSS
    const classRegex = new RegExp(`\\.${className}(?![a-zA-Z0-9_-])`, 'g');
    updatedCSS = updatedCSS.replace(classRegex, `.${newClassName}`);
    
    // Update HTML class attributes
    const htmlClassRegex = new RegExp(`class\\s*=\\s*["']([^"']*\\s)?${className}(\\s[^"']*)?["']`, 'gi');
    updatedHTML = updatedHTML.replace(htmlClassRegex, (match, before = '', after = '') => {
      const beforeTrimmed = before.trim();
      const afterTrimmed = after.trim();
      const newClassValue = [beforeTrimmed, newClassName, afterTrimmed].filter(Boolean).join(' ');
      return `class="${newClassValue}"`;
    });
  });
  
  // Rename IDs in CSS and HTML
  idSelectors.forEach(idName => {
    const newIdName = `${prefix}-${idName}`;
    
    // Update CSS
    const idRegex = new RegExp(`#${idName}(?![a-zA-Z0-9_-])`, 'g');
    updatedCSS = updatedCSS.replace(idRegex, `#${newIdName}`);
    
    // Update HTML id attributes
    const htmlIdRegex = new RegExp(`id\\s*=\\s*["']${idName}["']`, 'gi');
    updatedHTML = updatedHTML.replace(htmlIdRegex, `id="${newIdName}"`);
  });
  
  return { css: updatedCSS, html: updatedHTML };
};

/**
 * Scopes CSS rules to only apply within a specific container selector
 */
export const scopeCSSToContainer = (css: string, containerSelector: string): string => {
  // Remove any existing style tag wrappers if present
  const cleanCSS = css.replace(/<\/?style[^>]*>/gi, '');
  
  // Split CSS into individual rules
  const rules = cleanCSS.split('}').filter(rule => rule.trim());
  
  const scopedRules = rules.map(rule => {
    const trimmedRule = rule.trim();
    if (!trimmedRule) return '';
    
    // Find the selector part (before the opening brace)
    const openBraceIndex = trimmedRule.indexOf('{');
    if (openBraceIndex === -1) return trimmedRule + '}';
    
    const selector = trimmedRule.substring(0, openBraceIndex).trim();
    const properties = trimmedRule.substring(openBraceIndex);
    
    // Skip empty selectors or media queries
    if (!selector || selector.startsWith('@')) {
      return trimmedRule + '}';
    }
    
    // Split multiple selectors (comma-separated)
    const selectors = selector.split(',').map(s => s.trim());
    
    // Scope each selector
    const scopedSelectors = selectors.map(sel => {
      // Skip selectors that are already scoped or are pseudo-selectors
      if (sel.includes(containerSelector) || sel.startsWith(':') || sel.startsWith('::')) {
        return sel;
      }
      
      // Handle global selectors like body, html
      if (sel === 'body' || sel === 'html' || sel === '*') {
        return `${containerSelector}`;
      }
      
      // For other selectors, scope them within the container
      return `${containerSelector} ${sel}`;
    });
    
    return `${scopedSelectors.join(', ')} ${properties}}`;
  });
  
  return scopedRules.join('\n');
};

/**
 * Processes email HTML content to rename classes and scope CSS styles within a container
 */
export const processEmailContent = (htmlContent: string, containerClass: string = 'scoped-email-content'): { html: string; scopedCSS: string } => {
  if (!htmlContent) {
    return { html: '', scopedCSS: '' };
  }
  
  // Extract all style tags and their content
  const styleTagRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let extractedCSS = '';
  let processedHTML = htmlContent;
  
  // Extract CSS from style tags
  let match;
  while ((match = styleTagRegex.exec(htmlContent)) !== null) {
    extractedCSS += match[1] + '\n';
    // Remove the style tag from HTML
    processedHTML = processedHTML.replace(match[0], '');
  }
  
  // Rename CSS classes and IDs to prevent conflicts with app styles
  const { css: renamedCSS, html: renamedHTML } = renameCSSClasses(extractedCSS, processedHTML, 'email');
  
  // For emails with primarily inline styles (like Gmail), preserve inline styles
  // and only rename classes to avoid conflicts
  let finalHTML = renamedHTML;
  let inlineCSS = '';
  
  // Only extract inline styles if there are minimal CSS rules (mostly inline-styled emails)
  const cssRuleCount = (renamedCSS.match(/\{[^}]*\}/g) || []).length;
  
  if (cssRuleCount < 10) {
    // Email uses mostly inline styles - preserve them and add minimal scoping
    finalHTML = renamedHTML;
    inlineCSS = '';
  } else {
    // Email has significant CSS rules - convert inline styles to classes
    const inlineStyleRegex = /style\s*=\s*["']([^"']*?)["']/gi;
    let elementCounter = 0;
    
    finalHTML = renamedHTML.replace(inlineStyleRegex, (fullMatch, styleContent) => {
      const uniqueClass = `email-inline-style-${elementCounter++}`;
      inlineCSS += `.${uniqueClass} { ${styleContent} }\n`;
      
      // Check if element already has a class attribute
      const beforeStyle = fullMatch.substring(0, fullMatch.indexOf('style'));
      const afterStyle = fullMatch.substring(fullMatch.indexOf('style'));
      const withoutStyle = afterStyle.replace(/style\s*=\s*["'][^"']*["']/gi, '');
      
      const classMatch = beforeStyle.match(/class\s*=\s*["']([^"']*)["']/i);
      if (classMatch) {
        const existingClasses = classMatch[1];
        return beforeStyle.replace(/class\s*=\s*["'][^"']*["']/gi, `class="${existingClasses} ${uniqueClass}"`) + withoutStyle;
      } else {
        return beforeStyle + `class="${uniqueClass}" ` + withoutStyle;
      }
    });
  }
  
  // Combine renamed CSS and inline CSS
  const allCSS = renamedCSS + '\n' + inlineCSS;
  
  // Scope the CSS to the container (but preserve inline styles)
  const scopedCSS = scopeCSSToContainer(allCSS, `.${containerClass}`);
  
  return {
    html: finalHTML,
    scopedCSS: scopedCSS
  };
};

/**
 * Sanitizes email content by removing potentially harmful elements
 * while preserving formatting and scoping styles
 */
export const sanitizeAndScopeEmailContent = (htmlContent: string, containerClass: string = 'scoped-email-content'): { html: string; scopedCSS: string } => {
  if (!htmlContent) {
    return { html: '', scopedCSS: '' };
  }
  
  // Process the email content to extract and scope CSS
  const { html, scopedCSS } = processEmailContent(htmlContent, containerClass);
  
  // Remove potentially harmful elements but keep formatting
  let sanitizedHTML = html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags
    .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '') // Remove iframes
    .replace(/<object[^>]*>[\s\S]*?<\/object>/gi, '') // Remove object tags
    .replace(/<embed[^>]*>/gi, '') // Remove embed tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers
    .replace(/<!DOCTYPE[^>]*>/gi, '') // Remove DOCTYPE declarations
    .replace(/<\/?html[^>]*>/gi, '') // Remove html tags
    .replace(/<\/?head[^>]*>/gi, '') // Remove head tags
    .replace(/<\/?body[^>]*>/gi, '') // Remove body tags
    .replace(/<\/?title[^>]*>/gi, '') // Remove title tags
    .replace(/<meta[^>]*>/gi, ''); // Remove meta tags
  
  return {
    html: sanitizedHTML,
    scopedCSS: scopedCSS
  };
};
